'use client';

import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { FileText } from '@phosphor-icons/react';
import { useThrottledValue } from '@/lib/use-throttled-value';
import { capToLastLines, isPinnedToBottom, type StreamingToolBody } from '@/lib/streaming-tool-input';
import { cn } from '@/lib/utils';

// Deltas arrive fast (many per second). Coalesce commits so we re-render the
// live view at most ~every THROTTLE_MS instead of once per chunk — the same
// discipline StreamingHtmlPreview uses to avoid re-layout storms.
const THROTTLE_MS = 90;

// Cap the live view to the last N lines so a huge file streams into a bounded,
// fixed-height region. The full content renders in the normal card once the
// tool reaches input-available.
const MAX_LIVE_LINES = 300;

// Distance from the bottom (px) within which we consider the user "pinned" and
// keep auto-following. Above this they've scrolled up to read — stop following.
const PIN_THRESHOLD_PX = 32;

interface StreamingToolInputProps {
  body: StreamingToolBody;
  className?: string;
}

/**
 * Live code/text view of a tool's streaming input — watch the file being
 * written. Monospace, auto-follow scroll pinned to the tail (released when the
 * user scrolls up, re-engaged when they return to the bottom), a blinking
 * cursor at the end, a header showing the file/path, and a live line + char
 * count. Memoized and rendered into a fixed max-height scroll region so a
 * burst of deltas never re-lays-out the surrounding transcript.
 */
function StreamingToolInputImpl({ body, className }: StreamingToolInputProps) {
  const { content, title, language, label } = body;

  // Throttle the raw streaming content so we commit a bounded number of
  // renders per second. Trailing-only throttle => the final value is never
  // dropped when the stream ends.
  const throttled = useThrottledValue(content, THROTTLE_MS);
  const { text, totalLines, hiddenLines } = capToLastLines(throttled, MAX_LIVE_LINES);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Whether we should keep the view pinned to the bottom. Ref (not state) so
  // the scroll handler can flip it without triggering a render.
  const pinnedRef = useRef(true);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current = isPinnedToBottom(el.scrollHeight, el.scrollTop, el.clientHeight, PIN_THRESHOLD_PX);
  }, []);

  // After each committed content change, follow the tail iff still pinned.
  // useLayoutEffect so the jump happens before paint (no visible flicker).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [text]);

  const charCount = content.length;

  return (
    <div className={cn('flex flex-col overflow-hidden rounded-xl border border-border bg-muted/20', className)}>
      {/* Header — what's being written + live counts */}
      <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2 text-xs">
        <FileText weight="duotone" className="size-4 shrink-0 text-violet-400" aria-hidden />
        <span className="min-w-0 flex-1 truncate font-mono text-foreground/80">
          {title || label || 'Writing…'}
        </span>
        {language ? (
          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {language}
          </span>
        ) : null}
        <span className="shrink-0 tabular-nums font-mono text-[10px] text-muted-foreground/70">
          {totalLines.toLocaleString()} {totalLines === 1 ? 'line' : 'lines'} · {charCount.toLocaleString()} chars
        </span>
      </div>

      {/* Live code region — fixed height, scrollable, auto-following the tail */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="relative max-h-72 min-h-[4rem] overflow-auto bg-background/40"
      >
        {hiddenLines > 0 ? (
          <div className="sticky top-0 z-10 border-b border-border/40 bg-muted/60 px-3 py-1 text-[10px] text-muted-foreground/80 backdrop-blur-sm">
            … {hiddenLines.toLocaleString()} earlier {hiddenLines === 1 ? 'line' : 'lines'} hidden
          </div>
        ) : null}
        <pre className="whitespace-pre px-3 py-2 font-mono text-[11px] leading-relaxed text-foreground/85">
          {text}
          {/* Blinking cursor at the tail — reads as "actively typing" */}
          <span
            className="ml-px inline-block h-[1em] w-[0.5ch] translate-y-[0.15em] animate-pulse bg-violet-400/80 align-baseline"
            aria-hidden
          />
        </pre>
      </div>
    </div>
  );
}

export const StreamingToolInput = memo(StreamingToolInputImpl);
